import { PenLine, Upload, Camera, Clock, Users } from 'lucide-react';
import type { DocumentBlock } from '../../lib/documents';

/**
 * Read-only "how it looks" renderer for a document's blocks — a blank form view.
 * Reused by the preview modal now and the fill-out flow later.
 */
export default function DocumentRenderer({ blocks }: { blocks: DocumentBlock[] }) {
  return (
    <div className="space-y-5">
      {blocks.map((block) => (
        <BlockView key={block.id} block={block} />
      ))}
    </div>
  );
}

function ReqMark({ required }: { required?: boolean }) {
  return required ? <span className="text-red-500 ml-0.5">*</span> : null;
}

function BlockView({ block }: { block: DocumentBlock }) {
  const c = block.config || {};
  switch (block.type) {
    case 'heading': {
      const size = c.size === 'h1' ? 'text-2xl' : c.size === 'h3' ? 'text-base' : 'text-xl';
      return <div className={`${size} font-bold text-gray-900`}>{c.text || ''}</div>;
    }
    case 'rich_text':
      return <p className="text-[14px] text-gray-700 whitespace-pre-wrap leading-relaxed">{c.text || ''}</p>;
    case 'divider':
      return <hr className="border-gray-200" />;
    case 'image':
      return c.url
        ? <figure><img src={c.url} alt={c.caption || ''} className="max-h-64 rounded-lg" />{c.caption && <figcaption className="text-xs text-gray-500 mt-1">{c.caption}</figcaption>}</figure>
        : null;
    case 'checklist':
      return (
        <div>
          {c.label && <div className="text-[14px] font-semibold text-gray-800 mb-2">{c.label}<ReqMark required={block.required} /></div>}
          <ul className="space-y-1.5">
            {(c.items || []).map((it: any) => (
              <li key={it.id} className="flex items-center gap-2.5">
                <span className="h-4 w-4 rounded border border-gray-300 flex-shrink-0" />
                <span className="text-[14px] text-gray-700">{it.text}</span>
              </li>
            ))}
          </ul>
        </div>
      );
    case 'acknowledge':
      return (
        <label className="flex items-start gap-2.5">
          <span className="h-4 w-4 rounded border border-gray-300 mt-0.5 flex-shrink-0" />
          <span className="text-[14px] text-gray-700">{c.label}<ReqMark required={block.required} /></span>
        </label>
      );
    case 'multiple_choice':
      return (
        <div>
          <div className="text-[14px] font-semibold text-gray-800 mb-2">{c.label}<ReqMark required={block.required} /></div>
          <div className="space-y-1.5">
            {(c.options || []).map((opt: any) => (
              <label key={opt.id} className="flex items-center gap-2.5">
                <span className={`h-4 w-4 border border-gray-300 flex-shrink-0 ${c.multiple ? 'rounded' : 'rounded-full'}`} />
                <span className="text-[14px] text-gray-700">{opt.text}</span>
              </label>
            ))}
          </div>
        </div>
      );
    case 'short_text':
      return (
        <div>
          <div className="text-[14px] font-semibold text-gray-800 mb-1.5">{c.label}<ReqMark required={block.required} /></div>
          <div className="h-9 rounded-lg border border-gray-200 bg-gray-50" />
        </div>
      );
    case 'long_text':
      return (
        <div>
          <div className="text-[14px] font-semibold text-gray-800 mb-1.5">{c.label}<ReqMark required={block.required} /></div>
          <div className="h-20 rounded-lg border border-gray-200 bg-gray-50" />
        </div>
      );
    case 'number':
      return (
        <div>
          <div className="text-[14px] font-semibold text-gray-800 mb-1.5">{c.label}<ReqMark required={block.required} /></div>
          <div className="flex items-center gap-2">
            <div className="h-9 w-32 rounded-lg border border-gray-200 bg-gray-50" />
            {c.unit && <span className="text-[13px] text-gray-500">{c.unit}</span>}
          </div>
        </div>
      );
    case 'date':
    case 'time':
      return (
        <div>
          <div className="text-[14px] font-semibold text-gray-800 mb-1.5">{c.label}<ReqMark required={block.required} /></div>
          {block.type === 'date' && c.autoStampAtCompletion ? (
            <div className="inline-flex items-center gap-1.5 text-[13px] text-blue-600 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
              <Clock className="h-4 w-4" /> Date &amp; time stamped automatically at completion
            </div>
          ) : (
            <div className="h-9 w-44 rounded-lg border border-gray-200 bg-gray-50" />
          )}
        </div>
      );
    case 'signature':
      return (
        <div>
          <div className="text-[14px] font-semibold text-gray-800 mb-1.5">{c.label}<ReqMark required={block.required} /></div>
          <div className="h-24 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center text-xs text-gray-400"><PenLine className="h-4 w-4 mr-1.5" /> Sign here</div>
        </div>
      );
    case 'file_upload':
    case 'photo':
      return (
        <div>
          <div className="text-[14px] font-semibold text-gray-800 mb-1.5">{c.label}<ReqMark required={block.required} /></div>
          <div className="h-16 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center text-xs text-gray-400">
            {block.type === 'photo' ? <Camera className="h-4 w-4 mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}
            {block.type === 'photo' ? 'Add photo' : 'Upload file'}
          </div>
        </div>
      );
    case 'table': {
      const columns: { id: string; label: string }[] = Array.isArray(c.columns) ? c.columns : [];
      const rows: { id: string; cells: Record<string, string> }[] = Array.isArray(c.rows) ? c.rows : [];
      return (
        <div>
          {c.label && <div className="text-[14px] font-semibold text-gray-800 mb-1.5">{c.label}<ReqMark required={block.required} /></div>}
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-[13px] border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  {columns.map((col) => (
                    <th key={col.id} className="border border-gray-200 px-2 py-1.5 text-left font-semibold text-gray-700">{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    {columns.map((col) => (
                      <td key={col.id} className="border border-gray-200 px-2 py-2 text-gray-500 align-top">{row.cells?.[col.id] || ''}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {c.allowAddRows !== false && <div className="text-[11px] text-gray-400 mt-1">Rows can be added when completing.</div>}
        </div>
      );
    }
    case 'additional_signatures':
      return (
        <div>
          {c.label && <div className="text-[14px] font-semibold text-gray-800 mb-1.5">{c.label}<ReqMark required={block.required} /></div>}
          <div className="rounded-lg border-2 border-dashed border-gray-200 px-3 py-3 text-xs text-gray-400 flex items-center gap-2">
            <Users className="h-4 w-4" /> Names &amp; signatures can be added when completing
          </div>
        </div>
      );
    default:
      return null;
  }
}
